import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { lazy, Suspense } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import type { Deal } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardKpis } from "./DashboardKpis";
import { FirstActions } from "./FirstActions";
import { NeedsFollowUp } from "./NeedsFollowUp";
import { TodayList } from "./TodayList";
import { DriftRadar } from "./DriftRadar";
import { Page, PageHeader } from "../layout/Page";
import { FocusDashboard } from "./focus/FocusDashboard";
import { useDashboardPrefs, isOldLayout } from "./focus/useDashboardPrefs";

// Avtalegrafen drar @nivo (~90 kB) og står utenfor det man leser først, så
// den hentes etter at siden er tegnet — og vises bare når det finnes avtaler.
//
// Widgeteditoren bruker Tiptap/ProseMirror og lastes først med Oversikt.
const DashboardWidget = lazy(() =>
  import("./DashboardWidget").then((m) => ({ default: m.DashboardWidget })),
);
const DealsChart = lazy(() =>
  import("./DealsChart").then((m) => ({ default: m.DealsChart })),
);

// Tidsbasert hilsen (morgen/dag/kveld) for en personlig topp på Oversikt.
const greetingKey = (hour: number) => {
  if (hour < 6)
    return { key: "crm.dashboard.greeting.night", fallback: "God natt" };
  if (hour < 11)
    return { key: "crm.dashboard.greeting.morning", fallback: "God morgen" };
  if (hour < 18)
    return { key: "crm.dashboard.greeting.day", fallback: "God dag" };
  return { key: "crm.dashboard.greeting.evening", fallback: "God kveld" };
};

// Oversikt: den nye «Fokus»-oversikten, eller den gamle for den som har valgt
// «Gammel oversikt» i brukermenyen (tilgjengelig i to uker, til
// OLD_LAYOUT_UNTIL).
export const Dashboard = () => {
  const { layout, isPending } = useDashboardPrefs();
  if (isPending) return null;
  return isOldLayout(layout) ? <ClassicDashboard /> : <FocusDashboard />;
};

// Den gamle Oversikten: nøkkeltall og prosjektstyring kombinert med CRM-info,
// delte notater og lenker. Personlig hilsen og dato på toppen.
export const ClassicDashboard = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { total: totalDeal } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1 },
  });

  const now = new Date();
  const greeting = greetingKey(now.getHours());
  const firstName = identity?.fullName?.split(" ")[0];
  const dateLabel = now.toLocaleDateString("nb-NO", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <Page>
      <PageHeader
        title={`${translate(greeting.key, { _: greeting.fallback })}${
          firstName ? `, ${firstName}` : ""
        }`}
        description={dateLabel}
      />
      <div className="flex flex-col gap-8">
        <FirstActions />
        <DashboardKpis />
        {/* Tre like kolonner rett under nøkkeltallene: dagens gjøremål og de
            to widgetene. Dette er toppen av siden fordi det er det man
            faktisk kommer hit for — «hva haster» og «hva har vi satt opp
            selv». Kolonnene stables på smal skjerm.
            «I dag» (V9): alle daterte gjøremål i én prioritert liste. */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 2xl:grid-cols-3">
          <div className="xl:col-span-2 2xl:col-span-1">
            <TodayList />
          </div>
          <Suspense fallback={<Skeleton className="h-48 w-full rounded-xl" />}>
            <DashboardWidget
              widgetId={1}
              standardNavn={translate("crm.dashboard.widget.default_1", {
                _: "Delte notater",
              })}
            />
          </Suspense>
          <Suspense fallback={<Skeleton className="h-48 w-full rounded-xl" />}>
            <DashboardWidget
              widgetId={2}
              standardNavn={translate("crm.dashboard.widget.default_2", {
                _: "Nyttige lenker",
              })}
            />
          </Suspense>
        </div>
        {/* Radarene side om side: begge er lister over kundenavn med en kort
            begrunnelse, og de leses raskere ved siden av hverandre enn under
            hverandre. Stables på smal skjerm. DriftRadar skjuler seg når
            den er tom; da tar den gjenværende hele bredden i stedet for å
            stå i en halv kolonne med tomrom ved siden av. */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:*:only:col-span-2">
          <NeedsFollowUp />
          <DriftRadar />
        </div>
        {/* Avtalegrafen i full bredde — den trenger plassen. */}
        {totalDeal ? (
          <Suspense fallback={<Skeleton className="h-64 w-full" />}>
            <DealsChart />
          </Suspense>
        ) : null}
        {/* Siste aktivitet ligger nederst. */}
        <DashboardActivityLog />
      </div>
    </Page>
  );
};
